import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { FocusScope } from '@react-aria/focus';
import { colors } from '@ardoq/design-tokens';
import {
  ModalPortalContext,
  useFocusFirstElement,
  useKeydown,
} from '@ardoq/hooks';
import { handleEscapeButtonPressed } from '@ardoq/modal';
import { TRANSITION_TIME } from './atoms';
import { DrawerPortal } from './DrawerPortal';
import { useLastOpenChildren } from './useLastOpenChildren';
import { TransitionState, useTransitionState } from './useTransitionState';

export type AnimatedBottomDrawerProps = {
  isOpen: boolean;
  ariaLabel: string;
  onClickOutside?: VoidFunction;
  onEscPress?: VoidFunction;
  /**
   * Called once the exit animation has finished. For imperative callers that
   * would otherwise need a `setTimeout` to know when the drawer can unmount.
   */
  onExited?: VoidFunction;
  children: ReactNode;
};

const emptyKeyframes = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 0;
  }
`;

const mountingAnimation = css`
  animation: ${emptyKeyframes} 1ms;
`;

const Overlay = styled.div<{ $state: TransitionState }>`
  position: fixed;
  inset: 0;
  pointer-events: ${({ $state }) => ($state !== 'exiting' ? 'auto' : 'none')};
`;

const enteringBackdropStyles = css`
  opacity: 1;
`;

const exitingBackdropStyles = css`
  opacity: 0.001;
`;

const AnimatedBackdrop = styled.div<{ $state: TransitionState }>`
  position: fixed;
  inset: 0;
  pointer-events: none;
  opacity: 0;
  background-color: ${colors.bgOverlay};
  transition: opacity ${TRANSITION_TIME}ms ease;
  ${({ $state }) => $state === 'mounting' && mountingAnimation}
  ${({ $state }) => $state === 'entering' && enteringBackdropStyles}
  ${({ $state }) => $state === 'exiting' && exitingBackdropStyles}
`;

const BottomAnchor = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const enteringSheetStyles = css`
  transform: translateY(0);
`;

const AnimatedSheet = styled.div<{ $state: TransitionState }>`
  pointer-events: auto;
  transform: translateY(100%);
  transition: transform ${TRANSITION_TIME}ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
  &:focus {
    outline: none;
  }
  ${({ $state }) => $state === 'mounting' && mountingAnimation}
  ${({ $state }) => $state === 'entering' && enteringSheetStyles}
  /* 'exiting' transitions back to the default translateY(100%) */
`;

const DRAWER_HEADER_EXCLUDE_SELECTOR = '[data-drawer-header] *';

const useTopmostDialogEscapeKey = (
  onEscPress: VoidFunction | undefined,
  containerSelector: string,
  dialogRef: RefObject<HTMLDivElement | null>
) => {
  const handleEscape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !onEscPress || !dialogRef.current) return;
    const dialogs = Array.from(document.querySelectorAll('[role="dialog"]'));
    if (dialogs[dialogs.length - 1] !== dialogRef.current) return;
    return handleEscapeButtonPressed(onEscPress, containerSelector)(event);
  };

  useKeydown(handleEscape);
};

const useExitedCallback = (
  hasExited: boolean,
  onExited: VoidFunction | undefined
) => {
  const wasShown = useRef(false);

  useEffect(() => {
    if (hasExited && wasShown.current) onExited?.();
    wasShown.current = !hasExited;
  }, [hasExited, onExited]);
};

export const AnimatedBottomDrawer = ({
  isOpen,
  ariaLabel,
  onClickOutside,
  onEscPress,
  onExited,
  children,
}: AnimatedBottomDrawerProps) => {
  const [backdropState, updateBackdropState] = useTransitionState(isOpen);
  const [sheetState, updateSheetState] = useTransitionState(isOpen);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  );

  useTopmostDialogEscapeKey(onEscPress, String(AnimatedSheet), sheetRef);

  useFocusFirstElement({
    containerRef: sheetRef,
    shouldFocus: sheetState === 'entering',
    excludeSelector: DRAWER_HEADER_EXCLUDE_SELECTOR,
  });

  const hasExited =
    !isOpen && backdropState === 'exited' && sheetState === 'exited';

  const visibleChildren = useLastOpenChildren(isOpen, children, hasExited);

  useExitedCallback(hasExited, onExited);

  if (hasExited) return null;

  return (
    <DrawerPortal>
      <Overlay $state={sheetState} onClick={onClickOutside} />
      <AnimatedBackdrop
        $state={backdropState}
        onAnimationEnd={updateBackdropState}
        onTransitionEnd={updateBackdropState}
      />
      <BottomAnchor>
        <FocusScope contain restoreFocus autoFocus>
          <AnimatedSheet
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            $state={sheetState}
            inert={!isOpen}
            onAnimationEnd={updateSheetState}
            onTransitionEnd={updateSheetState}
          >
            <ModalPortalContext.Provider value={portalContainer}>
              {visibleChildren}
            </ModalPortalContext.Provider>
          </AnimatedSheet>
          <div ref={setPortalContainer} style={{ pointerEvents: 'auto' }} />
        </FocusScope>
      </BottomAnchor>
    </DrawerPortal>
  );
};
